/**
 * Krok UXV4c bod 3 — nahravani v prohlizeci (MediaRecorder) + uroven signalu
 * pro „vlnu" v predloze. Tenka obalka, aby obrazovka nezavisela primo na
 * prohlizecovem API (v testech se nahradi atrapou).
 */

export interface RecorderHandle {
  /** Uroven 0..1 pro vizualizaci (vola se z requestAnimationFrame). */
  level(): number;
  stop(): Promise<{ readonly blob: Blob; readonly durationS: number }>;
}

export interface Recorder {
  readonly supported: boolean;
  start(): Promise<RecorderHandle>;
}

/** Nejlepsi formát, ktery prohlizec umi (Safari mp4, ostatni webm/opus). */
function pickMimeType(): string | undefined {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return undefined;
  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

export const browserRecorder: Recorder = {
  supported: typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && navigator.mediaDevices !== undefined,
  async start() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType === undefined ? {} : { mimeType, audioBitsPerSecond: 32_000 });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    const startedAt = performance.now();
    // Kousky po 10 s: pri pádu stranky prijdeme nejvys o posledni kousek.
    recorder.start(10_000);

    let analyser: AnalyserNode | null = null;
    let context: AudioContext | null = null;
    try {
      context = new AudioContext();
      analyser = context.createAnalyser();
      analyser.fftSize = 256;
      context.createMediaStreamSource(stream).connect(analyser);
    } catch {
      analyser = null;
    }
    const buffer = new Uint8Array(128);

    return {
      level() {
        if (analyser === null) return 0;
        analyser.getByteTimeDomainData(buffer);
        let peak = 0;
        for (const v of buffer) peak = Math.max(peak, Math.abs(v - 128));
        return Math.min(1, peak / 64);
      },
      stop() {
        return new Promise((resolve) => {
          recorder.onstop = () => {
            stream.getTracks().forEach((track) => track.stop());
            void context?.close();
            resolve({
              blob: new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }),
              durationS: (performance.now() - startedAt) / 1000,
            });
          };
          recorder.stop();
        });
      },
    };
  },
};
